import React, { useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Input,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AuditLogDto, useAuditLogs } from '@shared/api/hooks/useAudit';

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
  PUBLISH: 'cyan',
  UNPUBLISH: 'orange',
  BAN: 'volcano',
  UNBAN: 'lime',
};

export function AuditPage(): React.ReactElement {
  const [filterEntity, setFilterEntity] = useState<string>('');
  const [filterUserId, setFilterUserId] = useState<string>('');
  const [filterEntityId, setFilterEntityId] = useState<string>('');
  const [page, setPage] = useState(1);
  const [drawerLog, setDrawerLog] = useState<AuditLogDto | null>(null);

  const {
    data: pageData,
    isLoading,
    refetch,
    isFetching,
  } = useAuditLogs({
    entity: filterEntity || undefined,
    userId: filterUserId || undefined,
    entityId: filterEntityId || undefined,
    page,
    limit: 30,
  });

  const data = pageData?.data ?? [];
  const meta = pageData?.meta;

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Audit log
          </Typography.Title>
          <Tag>{meta?.total ?? data.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Input
            placeholder="Entità (es. Match)"
            allowClear
            style={{ width: 160 }}
            value={filterEntity}
            onChange={(e) => {
              setFilterEntity(e.target.value);
              setPage(1);
            }}
          />
          <Input
            placeholder="User ID"
            allowClear
            style={{ width: 220 }}
            value={filterUserId}
            onChange={(e) => {
              setFilterUserId(e.target.value);
              setPage(1);
            }}
          />
          <Input
            placeholder="Entity ID"
            allowClear
            style={{ width: 220 }}
            value={filterEntityId}
            onChange={(e) => {
              setFilterEntityId(e.target.value);
              setPage(1);
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
        </Space>
      }
    >
      <Table<AuditLogDto>
        rowKey="id"
        loading={isLoading}
        dataSource={data}
        scroll={{ x: 'max-content' }}
        pagination={
          meta
            ? {
                current: meta.page,
                pageSize: meta.limit,
                total: meta.total,
                onChange: (p) => setPage(p),
                showSizeChanger: false,
              }
            : false
        }
        columns={[
          {
            title: 'Quando',
            dataIndex: 'createdAt',
            width: 170,
            render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm:ss'),
          },
          {
            title: 'Azione',
            dataIndex: 'action',
            width: 120,
            render: (v: string) => <Tag color={ACTION_COLORS[v] ?? 'default'}>{v}</Tag>,
          },
          { title: 'Entità', dataIndex: 'entity', width: 150 },
          {
            title: 'Entity ID',
            dataIndex: 'entityId',
            width: 280,
            render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
          },
          {
            title: 'User ID',
            dataIndex: 'userId',
            width: 280,
            render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
          },
          { title: 'IP', dataIndex: 'ipAddress', width: 130 },
          {
            title: '',
            width: 90,
            align: 'right',
            render: (_v, r) => (
              <Button size="small" onClick={() => setDrawerLog(r)}>
                Diff
              </Button>
            ),
          },
        ]}
      />

      <Drawer
        title="Diff cambio"
        placement="right"
        width={620}
        open={drawerLog !== null}
        onClose={() => setDrawerLog(null)}
      >
        {drawerLog ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text type="secondary">
              {drawerLog.action} su {drawerLog.entity} · {dayjs(drawerLog.createdAt).format('DD/MM/YYYY HH:mm:ss')}
            </Typography.Text>
            <pre
              style={{
                background: '#0d1117',
                color: '#e6edf3',
                padding: 16,
                borderRadius: 4,
                overflow: 'auto',
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              {JSON.stringify(drawerLog.changes ?? {}, null, 2)}
            </pre>
          </Space>
        ) : null}
      </Drawer>
    </Card>
  );
}
